import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import './i18n'; // Initialize i18n
import { AppConfigProvider } from "./lib/AppConfigContext";
import LoginPage from "./pages/LoginPage";
import LandingPage from "./pages/LandingPage";
import RegisterPage from "./pages/RegisterPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import DashboardPage from "./pages/DashboardPage";
import ConsultesPage from "./pages/ConsultesPage";
import ConsultaDetailPage from "./pages/ConsultaDetailPage";
import NewConsultaPage from "./pages/NewConsultaPage";
import CensusPage from "./pages/CensusPage";
import ScrutinyPage from "./pages/ScrutinyPage";
import ResultsPage from "./pages/ResultsPage";
import VotingPage from "./pages/VotingPage";
import VerifyVotePage from "./pages/VerifyVotePage";
import AuditVerifyPage from "./pages/public/AuditVerifyPage";
import KeyGenerationPage from "./pages/KeyGenerationPage";
import GenerateKeysPage from "./pages/GenerateKeysPage";
import SettingsPage from "./pages/SettingsPage";
// Scrutiny pages
import AdminScrutinyPage from "./pages/AdminScrutinyPage";
import CompleteScrutinyPage from "./pages/CompleteScrutinyPage";
import AdminHandVotePage from "./pages/AdminHandVotePage";
import HandVoteCompletePage from "./pages/HandVoteCompletePage";
// Admin pages
import AdminLoginPage from "./pages/admin/AdminLoginPage";
import AdminDashboardPage from "./pages/admin/AdminDashboardPage";
import AdminEntitiesPage from "./pages/admin/AdminEntitiesPage";
import AdminConsultesPage from "./pages/admin/AdminConsultesPage";
import AdminResultsPage from "./pages/admin/AdminResultsPage";
import AdminConfirmDeletionPage from "./pages/admin/AdminConfirmDeletionPage";
import AdminCancelDeletionPage from "./pages/admin/AdminCancelDeletionPage";
import AdminScrutinyRequestsPage from "./pages/admin/AdminScrutinyRequestsPage";
import AdminConfigPage from "./pages/admin/AdminConfigPage";
import AdminUsersPage from "./pages/admin/AdminUsersPage";
import AdminBlockchainPage from "./pages/admin/AdminBlockchainPage";
import AdminMaintenancePage from "./pages/admin/AdminMaintenancePage";
import AdminAuditPage from "./pages/admin/AdminAuditPage";
import EntityScrutinyPage from "./pages/EntityScrutinyPage";
import RegisterVotersPage from "./pages/entity/RegisterVotersPage";
import VoterSetPasswordPage from "./pages/VoterSetPasswordPage";
import VoterOnlineRegisterPage from "./pages/VoterOnlineRegisterPage";
import CensusCheckPage from "./pages/CensusCheckPage";
import LegalPage from "./pages/LegalPage";
import "./App.css";
import { useEffect } from "react";

const ProtectedRoute = ({ children }) => {
  const entityId = localStorage.getItem("entity_id");
  if (!entityId) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

const AdminProtectedRoute = ({ children }) => {
  const adminUser = localStorage.getItem("admin_username");
  if (!adminUser) {
    return <Navigate to="/admin/login" replace />;
  }
  return children;
};

function App() {
  // Listen for auth expired events from API interceptors
  useEffect(() => {
    const onEntityExpired = () => {
      localStorage.removeItem("entity_id");
      localStorage.removeItem("entity_name");
      localStorage.removeItem("entity_logo");
      localStorage.removeItem("entity_language");
      window.location.href = "/login";
    };
    const onAdminExpired = () => {
      localStorage.removeItem("admin_username");
      localStorage.removeItem("admin_name");
      window.location.href = "/admin/login";
    };
    window.addEventListener("auth:expired", onEntityExpired);
    window.addEventListener("admin:auth:expired", onAdminExpired);
    return () => {
      window.removeEventListener("auth:expired", onEntityExpired);
      window.removeEventListener("admin:auth:expired", onAdminExpired);
    };
  }, []);

  return (
    <AppConfigProvider>
    <div className="min-h-screen bg-slate-50">
      <BrowserRouter>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
          <Route path="/vote/:consultaId" element={<VotingPage />} />
          <Route path="/verify" element={<VerifyVotePage />} />
          <Route path="/audit-verify/:token" element={<AuditVerifyPage />} />
          <Route path="/voter/set-password/:token" element={<VoterSetPasswordPage />} />
          <Route path="/voter/register-online/:entityId" element={<VoterOnlineRegisterPage />} />
          <Route path="/census-check/:consultaId" element={<CensusCheckPage />} />
          
          {/* Public scrutiny routes */}
          <Route path="/admin/scrutiny/:token" element={<AdminScrutinyPage />} />
          <Route path="/admin/hand-vote/:token" element={<AdminHandVotePage />} />
          <Route path="/scrutiny/complete/:token" element={<CompleteScrutinyPage />} />
          <Route path="/hand-vote/complete/:token" element={<HandVoteCompletePage />} />
          
          {/* Admin routes */}
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route path="/admin/dashboard" element={
            <AdminProtectedRoute>
              <AdminDashboardPage />
            </AdminProtectedRoute>
          } />
          <Route path="/admin/entities" element={
            <AdminProtectedRoute>
              <AdminEntitiesPage />
            </AdminProtectedRoute>
          } />
          <Route path="/admin/consultes" element={
            <AdminProtectedRoute>
              <AdminConsultesPage />
            </AdminProtectedRoute>
          } />
          <Route path="/admin/results/:id" element={
            <AdminProtectedRoute>
              <AdminResultsPage />
            </AdminProtectedRoute>
          } />
          <Route path="/admin/scrutiny-requests" element={
            <AdminProtectedRoute>
              <AdminScrutinyRequestsPage />
            </AdminProtectedRoute>
          } />
          <Route path="/admin/config" element={
            <AdminProtectedRoute>
              <AdminConfigPage />
            </AdminProtectedRoute>
          } />
          <Route path="/admin/confirm-deletion/:token" element={<AdminConfirmDeletionPage />} />
          <Route path="/admin/cancel-deletion/:token" element={<AdminCancelDeletionPage />} />
          <Route path="/admin/users" element={
            <AdminProtectedRoute>
              <AdminUsersPage />
            </AdminProtectedRoute>
          } />
          <Route path="/admin/blockchain" element={
            <AdminProtectedRoute>
              <AdminBlockchainPage />
            </AdminProtectedRoute>
          } />
          <Route path="/admin/maintenance" element={
            <AdminProtectedRoute>
              <AdminMaintenancePage />
            </AdminProtectedRoute>
          } />
          <Route path="/admin/audit" element={
            <AdminProtectedRoute>
              <AdminAuditPage />
            </AdminProtectedRoute>
          } />
          <Route path="/admin" element={<Navigate to="/admin/login" replace />} />
          
          {/* Legal page - public */}
          <Route path="/legal" element={<LegalPage />} />

          {/* Protected routes */}
          <Route path="/" element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          } />
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          } />
          <Route path="/consultes" element={
            <ProtectedRoute>
              <ConsultesPage />
            </ProtectedRoute>
          } />
          <Route path="/consultes/new" element={
            <ProtectedRoute>
              <NewConsultaPage />
            </ProtectedRoute>
          } />
          <Route path="/consultes/:id" element={
            <ProtectedRoute>
              <ConsultaDetailPage />
            </ProtectedRoute>
          } />
          <Route path="/consultes/:id/census" element={
            <ProtectedRoute>
              <CensusPage />
            </ProtectedRoute>
          } />
          <Route path="/consultes/:id/scrutiny" element={
            <ProtectedRoute>
              <ScrutinyPage />
            </ProtectedRoute>
          } />
          <Route path="/consultes/:id/results" element={
            <ProtectedRoute>
              <ResultsPage />
            </ProtectedRoute>
          } />
          <Route path="/keys" element={
            <ProtectedRoute>
              <KeyGenerationPage />
            </ProtectedRoute>
          } />
          <Route path="/generate-keys" element={
            <ProtectedRoute>
              <GenerateKeysPage />
            </ProtectedRoute>
          } />
          <Route path="/escrutinis" element={
            <ProtectedRoute>
              <EntityScrutinyPage />
            </ProtectedRoute>
          } />
          <Route path="/settings" element={
            <ProtectedRoute>
              <SettingsPage />
            </ProtectedRoute>
          } />
          <Route path="/registre" element={
            <ProtectedRoute>
              <RegisterVotersPage />
            </ProtectedRoute>
          } />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
    </AppConfigProvider>
  );
}

export default App;
